import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Grid
} from '@mui/material';

const EditSiteContentDialog = ({ open, onClose, content, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    value_en: '',
    value_fin: '',
    value_fr: '',
    value_de: ''
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fin', label: 'Finnish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' }
  ];

  useEffect(() => {
    if (content) {
      setFormData({
        value_en: content.value_en || '',
        value_fin: content.value_fin || '',
        value_fr: content.value_fr || '',
        value_de: content.value_de || ''
      });
      setError('');
      setSuccess(false);
      setActiveTab(0);
    }
  }, [content]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!content) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        content_key: content.content_key,
        value_en: formData.value_en,
        value_fin: formData.value_fin,
        value_fr: formData.value_fr,
        value_de: formData.value_de,
        description: content.description
      };

      console.log('Sending payload:', JSON.stringify(payload));

      const response = await fetch('https://elnaf.fi/new/api/update_site_content.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      console.log('Data received:', data);
      
      // Check for success - backend might return different messages
      if (response.ok && (data.status === 'success' || data.message?.includes('uğurla') || data.message?.includes('updated'))) {
        setSuccess(true);
        console.log('Success detected! Will close in 1.5 seconds');
        // Show success for 1.5 seconds then close
        setTimeout(() => {
          console.log('Timeout reached, calling onSave');
          if (onSave) {
            console.log('Calling onSave now');
            onSave(); // This will refresh and close
          }
        }, 1500);
      } else {
        setError(data.message || 'Failed to update content');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !success) {
      setError('');
      setSuccess(false);
      setActiveTab(0);
      onClose();
    }
  };

  if (!content) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Site Content
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Key: <strong>{content.content_key}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {content.description}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Content updated successfully!
            </Alert>
          )}

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            {languages.map((lang, index) => (
              <Tab key={lang.code} label={lang.label} />
            ))}
          </Tabs>

          {languages.map((lang, index) => (
            <Box
              key={lang.code}
              hidden={activeTab !== index}
              sx={{ pt: 2 }}
            >
              <TextField
                fullWidth
                label={`Content (${lang.label})`}
                name={`value_${lang.code}`}
                value={formData[`value_${lang.code}`]}
                onChange={handleChange}
                multiline
                rows={4}
                variant="outlined"
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Saved!' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSiteContentDialog;